import type { Direction, GridPoint } from '../types';
import { communityPointKey } from '../data/community';

const STEPS: Array<{ direction: Direction; delta: GridPoint }> = [
  { direction: 'up', delta: { x: 0, y: -1 } },
  { direction: 'right', delta: { x: 1, y: 0 } },
  { direction: 'down', delta: { x: 0, y: 1 } },
  { direction: 'left', delta: { x: -1, y: 0 } },
];

export interface RouteGuidance {
  path: GridPoint[];
  preview: GridPoint[];
  direction?: Direction;
  message: string;
  turnPoint?: GridPoint;
}

function directionBetween(from: GridPoint, to: GridPoint): Direction | undefined {
  return STEPS.find(({ delta }) => from.x + delta.x === to.x && from.y + delta.y === to.y)?.direction;
}

export function calculateRouteGuidance(paths: Set<string>, start: GridPoint, target: GridPoint, blocked = new Set<string>()): RouteGuidance {
  const startKey = communityPointKey(start);
  const targetKey = communityPointKey(target);
  const queue = [startKey];
  const visited = new Set([startKey]);
  const prior = new Map<string, string>();

  while (queue.length) {
    const key = queue.shift();
    if (!key || key === targetKey) break;
    const [x = 0, y = 0] = key.split(',').map(Number);
    STEPS.forEach(({ delta }) => {
      const next = { x: x + delta.x, y: y + delta.y };
      const nextKey = communityPointKey(next);
      if (!paths.has(nextKey) || blocked.has(nextKey) || visited.has(nextKey)) return;
      visited.add(nextKey);
      prior.set(nextKey, key);
      queue.push(nextKey);
    });
  }

  if (!visited.has(targetKey)) return { path: [start], preview: [start], message: 'TAKE THE OPEN ROAD' };
  const path: GridPoint[] = [];
  let cursor = targetKey;
  while (true) {
    const [x = 0, y = 0] = cursor.split(',').map(Number);
    path.unshift({ x, y });
    if (cursor === startKey) break;
    const previous = prior.get(cursor);
    if (!previous) break;
    cursor = previous;
  }

  const direction = path[1] ? directionBetween(path[0]!, path[1]!) : undefined;
  let turnIndex = -1;
  for (let index = 1; index < path.length - 1; index += 1) {
    if (directionBetween(path[index - 1]!, path[index]!) !== directionBetween(path[index]!, path[index + 1]!)) {
      turnIndex = index;
      break;
    }
  }
  const turnDirection = turnIndex >= 0 && path[turnIndex + 1] ? directionBetween(path[turnIndex]!, path[turnIndex + 1]!) : direction;
  const message = turnIndex >= 0 && turnDirection
    ? `GO ${turnDirection.toUpperCase()} AT THE CORNER`
    : direction ? `TAKE THE OPEN ROAD ${direction.toUpperCase()}` : 'DESTINATION AHEAD';

  return {
    path,
    preview: path.slice(0, Math.min(path.length, Math.max(4, turnIndex >= 0 ? turnIndex + 2 : 5))),
    direction,
    message,
    turnPoint: turnIndex >= 0 ? path[turnIndex] : undefined,
  };
}
